import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { http, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const schema = z.object({
  name: z.string().min(2, "Please enter your full name"),
  phone: z
    .string()
    .min(7, "Please enter a valid phone number")
    .regex(/^[0-9+\-\s]+$/, "Only digits, +, and - allowed"),
  email: z
    .string()
    .email("Enter a valid email")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  class_level: z.enum(["Class 11", "Class 12", "Dropper"]),
  mentorship_type: z.enum(["1:1", "Group", "Either"]),
  current_score: z.string().optional(),
  goal: z.string().optional(),
});

export default function ApplicationForm({ open, onOpenChange, source = "hero" }) {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      class_level: "Class 12",
      mentorship_type: "1:1",
      current_score: "",
      goal: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = form;

  const classLevel = watch("class_level");
  const mentorshipType = watch("mentorship_type");

  const onSubmit = async (data) => {
    try {
      await http.post("/applications", { ...data, source });
      setSubmitted(true);
      toast.success("Application received. Suraj sir's team will call you shortly.");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not submit. Please try again."
      );
    }
  };

  const handleClose = (val) => {
    if (!val) {
      setTimeout(() => {
        setSubmitted(false);
        reset();
      }, 300);
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-[#0f0f0f] border border-white/10 text-white max-w-lg"
        data-testid="application-dialog"
      >
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#FFD700] font-semibold mb-1">
                <span className="inline-block w-2 h-2 rounded-full bg-[#FFD700] animate-pulse-soft" />
                Limited Seats — {source === "hero" ? "Apply Now" : "Mentorship Application"}
              </div>
              <DialogTitle className="font-display text-3xl md:text-4xl tracking-tight text-white">
                Apply for JEE Maths Mentorship
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Fill this form. Our team will contact you within 24 hours for a short fit call.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="name" className="text-gray-300 text-xs uppercase tracking-wider">
                  Full Name*
                </Label>
                <Input
                  id="name"
                  data-testid="application-name-input"
                  {...register("name")}
                  className="mt-1 bg-[#1a1a1a] border-white/10 text-white focus-visible:ring-[#FFD700]"
                  placeholder="Your full name"
                />
                {errors.name && (
                  <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-gray-300 text-xs uppercase tracking-wider">
                    WhatsApp / Phone*
                  </Label>
                  <Input
                    id="phone"
                    data-testid="application-phone-input"
                    {...register("phone")}
                    className="mt-1 bg-[#1a1a1a] border-white/10 text-white focus-visible:ring-[#FFD700]"
                    placeholder="+91 98XXX XXXXX"
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-400 mt-1">{errors.phone.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 text-xs uppercase tracking-wider">
                    Email (optional)
                  </Label>
                  <Input
                    id="email"
                    data-testid="application-email-input"
                    type="email"
                    {...register("email")}
                    className="mt-1 bg-[#1a1a1a] border-white/10 text-white focus-visible:ring-[#FFD700]"
                    placeholder="you@example.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">
                    Current Class*
                  </Label>
                  <Select
                    value={classLevel}
                    onValueChange={(v) => setValue("class_level", v)}
                  >
                    <SelectTrigger
                      data-testid="application-class-select"
                      className="mt-1 bg-[#1a1a1a] border-white/10 text-white"
                    >
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                      <SelectItem value="Class 11">Class 11</SelectItem>
                      <SelectItem value="Class 12">Class 12</SelectItem>
                      <SelectItem value="Dropper">Dropper</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">
                    Mentorship Type*
                  </Label>
                  <Select
                    value={mentorshipType}
                    onValueChange={(v) => setValue("mentorship_type", v)}
                  >
                    <SelectTrigger
                      data-testid="application-type-select"
                      className="mt-1 bg-[#1a1a1a] border-white/10 text-white"
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                      <SelectItem value="1:1">1:1 Personal</SelectItem>
                      <SelectItem value="Group">Small Group</SelectItem>
                      <SelectItem value="Either">Either / Need Advice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="current_score" className="text-gray-300 text-xs uppercase tracking-wider">
                  Current Mock Score / Percentile (optional)
                </Label>
                <Input
                  id="current_score"
                  data-testid="application-score-input"
                  {...register("current_score")}
                  className="mt-1 bg-[#1a1a1a] border-white/10 text-white focus-visible:ring-[#FFD700]"
                  placeholder="e.g. 82 percentile / 150 marks"
                />
              </div>

              <div>
                <Label htmlFor="goal" className="text-gray-300 text-xs uppercase tracking-wider">
                  What's your biggest Maths struggle? (optional)
                </Label>
                <Textarea
                  id="goal"
                  data-testid="application-goal-input"
                  {...register("goal")}
                  className="mt-1 bg-[#1a1a1a] border-white/10 text-white focus-visible:ring-[#FFD700]"
                  placeholder="Speed, accuracy, calculus, panic during tests..."
                  rows={3}
                />
              </div>

              <DialogFooter className="mt-2">
                <Button
                  type="submit"
                  data-testid="application-submit-button"
                  disabled={isSubmitting}
                  className="w-full bg-[#FFD700] hover:bg-[#E6C200] text-black font-semibold uppercase tracking-wide h-12 text-base"
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                    </span>
                  ) : (
                    "Apply for Mentorship"
                  )}
                </Button>
              </DialogFooter>
              <p className="text-center text-[11px] text-gray-500 uppercase tracking-widest">
                Limited Seats • Serious Aspirants Only
              </p>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="application-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#FFD700]" />
            </div>
            <h3 className="font-display text-3xl tracking-tight">Application Received</h3>
            <p className="text-gray-400 mt-2">
              Our team will reach out within <span className="text-white font-medium">24 hours</span> for a
              short fit call. Keep your phone nearby.
            </p>
            <Button
              data-testid="application-close-button"
              onClick={() => handleClose(false)}
              className="mt-6 bg-white/10 hover:bg-white/20 text-white"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
